import { gql, useMutation, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { useSelector } from '~Hooks';
import { GetPrinters } from './__queries__';

const GET_PRINTERS = gql`
  query GetPrinters($venueId: ID!) {
    Venue(id: $venueId) {
      id

      printers {
        id
        name
        type
      }
    }
  }
`;

const SET_PRINTER_NAME = gql`
  mutation SetPrinterName($printerId: String!, $venueId: String!, $name: String!, $type: String!) {
    addPrinter(id: $printerId, name: $name, venueId: $venueId, type: $type, stations: []) {
      success
      message
    }
  }
`;

/**
 * Printers have very difficult names like "F7A189EE" or "198.168.0.123".
 * The printer service in the backend tries to help the user by attaching names
 * to the printers, that we can then use for easy identification when setting up
 * a new device.
 */
export const usePrinterApi = () => {
  const venueId = useSelector((state) => state.venueId!);

  const printersQuery = useQuery<GetPrinters>(GET_PRINTERS, {
    variables: {
      venueId,
    },
    skip: !venueId,
  });

  const [addPrinterMutation] = useMutation(SET_PRINTER_NAME, {
    refetchQueries: ['GetPrinters'],
  });

  const api = useMemo(() => {
    const printers = printersQuery.data?.Venue?.printers ?? [];

    /**
     * Printers can have the exact same ID across different venues, so they are stored
     * in <id>|<venueId> format. Here we map the printers to a more convenient format.
     */
    const formattedPrinters = printers.map((p) => ({
      id: p.id.split('|')[0],
      name: p.name,
      type: p.type,
    }));

    /**
     * Checks if a printer exists in the backend.
     *
     * @param id The ID of the printer
     * @returns True if the printer exists in the backend
     */
    function exists(id: string) {
      return formattedPrinters.some((p) => p.id === id);
    }

    /**
     * Adds (or updates) a printer in the backend.
     *
     * @param id The ID of the printer (like 192.168.0.123)
     * @param name The name of the printer
     * @param type
     * @returns
     */
    function addPrinter(input: { id: string; name: string; type: string }) {
      return addPrinterMutation({
        variables: {
          printerId: input.id,
          venueId,
          name: input.name,
          type: input.type,
        },
      });
    }

    /**
     * Sets the name of a printer.
     *
     * The "add" mutation is practically an upsert. So we just
     * "add" the same printer again.
     *
     * @param input ID and name of the printer to set
     * @returns A promise for the mutation
     */
    function setPrinterName(input: { id: string; name: string }) {
      const printer = formattedPrinters.find((p) => p.id === input.id);

      if (!printer) {
        throw new Error(`Printer with id ${input.id} not found`);
      }

      return addPrinterMutation({
        variables: {
          printerId: input.id,
          venueId,
          name: input.name,
          type: printer.type,
        },
      });
    }

    /**
     * Gets the name of a printer from its ID.
     *
     * @param id The ID of the printer
     * @returns The name if one is set, otherwise null
     */
    function getNameFromId(id: string): string | null {
      const printer = formattedPrinters.find((p) => p.id === id);

      if (!printer) {
        return null;
      }

      return printer.name;
    }

    return {
      exists,
      addPrinter,
      getNameFromId,
      setPrinterName,
      printers,
      loading: printersQuery.loading,
    };
  }, [printersQuery, venueId]);

  return api;
};
